import axios from 'axios';
import isUrl from 'is-url';
import notification from '../utils/notification';
import { FETCH_PAYMENT_DETAILS, FETCH_USER } from './types';
import getWebchatApiUrl from '../utils/getWebchatUrl';

const formatPaymentDetails = (paymentDetails) => {
	paymentDetails.isLinkPayment = false;

	if (paymentDetails.type === 'link') {
		paymentDetails.paymentDetails = 'Link';
		paymentDetails.isLinkPayment = true;
	} else if (paymentDetails.type === 'acss_debit') {
		const bank = paymentDetails.bankName || 'Bank';
		paymentDetails.paymentDetails = `${bank} ••••${paymentDetails.last4}`;
	} else {
		paymentDetails.paymentDetails = `${paymentDetails.brand} ••••${paymentDetails.last4} ${paymentDetails.exp_month}/${paymentDetails.exp_year}`;
	}
};

export const setNewProfilePictureUrl =
	(profilePictureUrl) => async (dispatch) => {
		try {
			const res = await axios.post('/api/update-profile-picture-url', {
				profilePictureUrl,
			});

			dispatch({ type: FETCH_USER, payload: res.data });

			const title = 'Success';
			const message = profilePictureUrl
				? 'Updated your profile picture'
				: 'Removed your profile picture';
			const error = false;

			return dispatch(notification(title, message, error));
		} catch (err) {
			console.log(err);
		}
	};

export const updateCustomReviewInvitationText =
	({ customText }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				'/api/update-custom-review-invitation-text',
				{
					customText,
				},
			);

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your custom text.',
						true,
					),
				);
			}

			const title = 'Success';
			const message = 'Updated your custom invitation text';
			const error = false;

			return dispatch(notification(title, message, error));
		} catch {
			return dispatch(
				notification(
					'Error',
					'Sorry, there was an error updating your custom invitation text.',
					true,
				),
			);
		}
	};

export const updateUserSignature =
	({ signature }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-user-signature', {
				signature,
			});

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your signature.',
						true,
					),
				);
			}

			dispatch({ type: FETCH_USER, payload: res.data });

			const title = 'Success';
			const message = 'Updated your signature';
			const error = false;

			return dispatch(notification(title, message, error));
		} catch {
			return dispatch(
				notification(
					'Error',
					'Sorry, there was an error updating your signature.',
					true,
				),
			);
		}
	};

export const updateTwoFactorAuthenticationPhoneNumber =
	({ phoneNumber }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				'/api/update-two-factor-authentication-number',
				{
					phoneNumber,
				},
			);

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your 2FA settings.',
						true,
					),
				);
			}

			dispatch({ type: FETCH_USER, payload: res.data });

			const title = 'Success';
			const message = 'Updated your 2FA settings';
			const error = false;

			return dispatch(notification(title, message, error));
		} catch {
			return dispatch(
				notification(
					'Error',
					'Sorry, there was an error updating your 2FA settings.',
					true,
				),
			);
		}
	};

export const updateHasOwnIsReadIndicators =
	({ hasOwnIsReadIndicators }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				'/api/update-has-own-is-read-indicators',
				{
					hasOwnIsReadIndicators,
				},
			);

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your read indicators.',
						true,
					),
				);
			}

			dispatch({ type: FETCH_USER, payload: res.data });

			const title = 'Success';
			const message = 'You will now see your own read indicators';
			const error = false;

			return dispatch(notification(title, message, error));
		} catch {
			return dispatch(
				notification(
					'Error',
					'Sorry, there was an error updating your read indicators.',
					true,
				),
			);
		}
	};

export const updateEmail = (user) => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-email', user);

		if (res.data.error) {
			dispatch(
				notification(res.data.title, res.data.message, res.data.error),
			);
			return { error: true };
		}
		return window.location.replace(
			`https://${window.location.hostname}/login`,
		);
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your email.',
				true,
			),
		);
	}
};

export const updateBusinessEmail =
	(newEmail, confirmNewEmail) => async (dispatch) => {
		try {
			const res = await axios.post('/api/update-business-email', {
				newEmail,
				confirmNewEmail,
			});

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your business email.',
						true,
					),
				);
			}

			dispatch({ type: FETCH_USER, payload: res.data });

			const title = 'Success';
			const message = 'Updated Business Email';
			const error = false;

			return dispatch(notification(title, message, error));
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your email.',
					true,
				),
			);
		}
	};

export const updateBillingEmail = (newEmail) => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-billing-email', { newEmail });
		const { title, message, error } = res.data;

		return dispatch(notification(title, message, error));
	} catch {
		return dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
	}
};

export const updatePassword =
	({ password, newPassword, confirmNewPassword }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-password', {
				password,
				newPassword,
				confirmNewPassword,
			});

			if (res.data.notification) {
				dispatch(
					notification(
						res.data.title,
						res.data.message,
						res.data.error,
					),
				);
				return { error: res.data.error };
			}
			return { error: false };
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true,
				),
			);
		}
	};

export const updateUserName =
	({ name }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-name', { name });

			if (res.data && res.data.error) {
				dispatch(
					notification(
						'Error',
						res.data.message ||
							'There was an error updating your name.',
						true,
					),
				);
				return false;
			}

			dispatch({ type: FETCH_USER, payload: res.data });
			dispatch(
				notification('Success', 'Updated your name', false),
			);
			return true;
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your name.',
					true,
				),
			);
			return false;
		}
	};

export const updateName = (newName) => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-business-name', { newName });
		dispatch({ type: FETCH_USER, payload: res.data });
		const error = newName !== res.data.name;

		const title = error ? 'Error' : 'Success';
		const message = error
			? 'Could not update business name. Please try again'
			: 'Updated business name';
		dispatch(notification(title, message, error));
		return !error;
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
		return false;
	}
};

export const updateLegalBusinessName = (newName) => async (dispatch) => {
	try {
		const res = await axios.post('/api/settings/legal-business-name', {
			newName,
		});

		const { error, message } = res.data;

		const title = error ? 'Error' : 'Success';
		const notificationMessage =
			(error && message) || 'Updated legal business name';
		dispatch(notification(title, notificationMessage, error));
		return !error;
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
		return false;
	}
};

export const updateLegalBusinessAddress = (address) => async (dispatch) => {
	try {
		const res = await axios.post('/api/settings/legal-business-address', {
			address,
		});

		const { error, message } = res.data;

		const title = error ? 'Error' : 'Success';
		const notificationMessage =
			(error && message) || 'Updated legal business address';
		dispatch(notification(title, notificationMessage, error));
		return !error;
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
		return false;
	}
};

export const updateTimezone =
	({ timezone }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-timezone', { timezone });

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your timezone.',
						true,
					),
				);
			}

			dispatch(notification('Success', 'Updated timezone.', false));
			return dispatch({ type: FETCH_USER, payload: res.data });
		} catch (err) {
			console.log('Error updating timezone', err);
			dispatch(
				notification(
					'Error',
					'There was an error updating your timezone.',
					true,
				),
			);
		}
	};

export const updateHolidayHours =
	({ holidays, template, timezone }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-holidays', {
				holidays,
				template,
				timezone,
			});

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your settings.',
						true,
					),
				);
			}

			return dispatch(
				notification('Success', 'Updated holidays.', false),
			);
		} catch (err) {
			console.log('Error updating holiday hours', err);
			return dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true,
				),
			);
		}
	};

export const updateBusinessHours =
	({ days, template }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-business-hours', {
				days,
				template,
			});

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your settings.',
						true,
					),
				);
			}

			dispatch(notification('Success', 'Updated hours.', false));
			return dispatch({ type: FETCH_USER, payload: res.data });
		} catch (err) {
			console.log('Error updating business hours', err);
		}
	};

export const updateIsDripCampaignActive =
	({ isDripCampaignActive, template }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				'/api/update-is-drip-campaign-active',
				{
					isDripCampaignActive,
					template,
				},
			);

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your settings.',
						true,
					),
				);
			}

			dispatch(notification('Success', 'Updated drip campaign.', false));
			return dispatch({ type: FETCH_USER, payload: res.data });
		} catch (err) {
			console.log('Error updating business hours', err);
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true,
				),
			);
		}
	};

export const updateAutoReplyTemplate =
	({ template, hasFollowUpTemplate, followUpTemplate }) =>
	async (dispatch) => {
		try {
			const res = await axios(
				`${getWebchatApiUrl(
					window.location.href,
				)}/api/webchat/update-auto-reply-template`,
				{
					method: 'post',
					data: { template, hasFollowUpTemplate, followUpTemplate },
					withCredentials: true,
				},
			);

			if (res.data && res.data.error) {
				return notification(
					'Error',
					'There was an error updating your settings.',
					true,
				);
			}

			return dispatch(
				notification(
					'Success',
					'Your auto reply message has been updated',
					false,
				),
			);
		} catch (err) {
			return console.log('error updating auto reply message: ', err);
		}
	};

export const updateReviewUrl = (newReviewUrl) => async (dispatch) => {
	try {
		if (
			newReviewUrl !== undefined &&
			newReviewUrl !== '' &&
			!isUrl(newReviewUrl)
		) {
			return dispatch(notification('Error', 'Invalid Url', true));
		}

		if (newReviewUrl === undefined) {
			newReviewUrl = '';
		}

		const res = await axios.post('/api/update-review-url', {
			newReviewUrl,
		});
		dispatch({ type: FETCH_USER, payload: res.data });

		const error = newReviewUrl !== res.data.reviewUrl;
		const title = error ? 'Error' : 'Success';
		const message = error
			? 'Could not google review url. Please try again'
			: 'Updated google review url!';
		dispatch(notification(title, message, error));
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
	}
};
export const removeGMBLocation = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/remove-GMB-location');
		dispatch({ type: FETCH_USER, payload: res.data });

		const error = res.data.googleLocationPath !== '';
		const title = error ? 'Error' : 'Success';
		const message = error
			? 'Could not update GMB Location Please try again'
			: 'Updated GMB Location!';
		dispatch(notification(title, message, error));
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
	}
};

export const updateGbpTextMessagingNumber =
	({ phoneNumber, locationId, locationName } = {}) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-gbp-text-messaging', {
				phoneNumber,
				locationId,
				locationName,
			});

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						res.data.message ||
							'There was an error updating your Google settings.',
						true,
					),
				);
			}

			return dispatch(
				notification(
					'Success',
					'Updated Google Business Profile text message number!',
					false,
				),
			);
		} catch {
			return dispatch(
				notification(
					'Error',
					'Sorry, there was an error updating your Google settings.',
					true,
				),
			);
		}
	};

export const updateFacebookReviewUrl =
	(facebookReviewUrl) => async (dispatch) => {
		try {
			if (
				facebookReviewUrl !== undefined &&
				facebookReviewUrl !== '' &&
				!isUrl(facebookReviewUrl)
			) {
				return dispatch(notification('Error', 'Invalid Url', true));
			}

			if (facebookReviewUrl === undefined) {
				facebookReviewUrl = '';
			}

			const res = await axios.post('/api/update-facebook-review-url', {
				facebookReviewUrl,
			});
			dispatch({ type: FETCH_USER, payload: res.data });

			const error = facebookReviewUrl !== res.data.facebookReviewUrl;
			const title = error ? 'Error' : 'Success';
			const message = error
				? 'Could not update facebook url. Please try again'
				: 'Updated facebook url!';
			dispatch(notification(title, message, error));
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true,
				),
			);
		}
	};

export const addLightspeedToChekkit =
	({ _integrationApp, dealerId, username, password }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/add-lightspeed-to-chekkit', {
				_integrationApp,
				dealerId,
				username,
				password,
			});

			if (res.data && res.data.error) {
				const message =
					res.data.message ||
					'Could not connect Lightspeed. Check your Dealer ID and Web Service API credentials.';
				// Show under the form fields only — avoid a duplicate toast for
				// credential guidance customers need to read carefully.
				return { error: true, message };
			}

			dispatch(
				notification(
					'Success',
					'Lightspeed has been added to Chekkit. Completed sales and service jobs will sync automatically.',
					false,
				),
			);

			window.location.href = '/settings/crm-tools';
			return { success: true };
		} catch (err) {
			console.log(err);
			const message = 'There was an error adding Lightspeed to Chekkit.';
			dispatch(notification('Error', message, true));
			return { error: true, message };
		}
	};

export const updateCrmSoftware = (crmSoftware) => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-crm-software', {
			crmSoftware,
		});

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated crm software. Thank you!';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true,
				),
			);
		}
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
	}
};

export const updateBusinessAddress = (address) => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-business-address', {
			address,
		});

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated address';
			dispatch(notification(title, message, false));
			return true;
		}

		const errorMessage =
			(res.data && res.data.message) ||
			'There was an error updating your settings.';
		dispatch(notification('Error', errorMessage, true));
		return false;
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
		return false;
	}
};

export const setMonthlyGoal = (monthlyGoalVal) => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-monthly-goal', {
			monthlyGoalVal,
		});

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated monthly goal!';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true,
				),
			);
		}
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
	}
};

export const updatePhone = (newPhone) => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-phone', { newPhone });

		if (!res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			dispatch(
				notification('Success', 'Phone number updated!', false),
			);
			return true;
		}

		dispatch(notification('Error', 'Could not update phone', true));
		return false;
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
		return false;
	}
};

export const fetchPaymentDetails = () => async (dispatch) => {
	const res = await axios.get('/api/payment-details');
	formatPaymentDetails(res.data);
	dispatch({ type: FETCH_PAYMENT_DETAILS, payload: res.data });
};

export const updatePaymentMethod =
	({ paymentMethodId, showNotification = true }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-payment-method', {
				paymentMethodId,
			});

			if (res.data && res.data.error) {
				if (showNotification) {
					dispatch(
						notification(
							'Error',
							'There was an error updating your payment details.',
							true,
						),
					);
				}

				return {
					error: true,
					message:
						res.data.message ||
						'There was an error updating your payment information. Please refresh the page and try again.',
				};
			}

			const getPayment = await axios.get('/api/payment-details');

			formatPaymentDetails(getPayment.data);

			dispatch({ type: FETCH_PAYMENT_DETAILS, payload: getPayment.data });

			if (showNotification) {
				const paymentMethodLabel =
					getPayment.data.type === 'acss_debit'
						? `pre-authorized debit ending in ${getPayment.data.last4}`
						: getPayment.data.type === 'link'
							? 'Link'
							: `card ending in ${getPayment.data.last4}`;

				dispatch(
					notification(
						'Success',
						`Updated payment method to ${paymentMethodLabel}`,
						false,
					),
				);
			}

			return { error: false };
		} catch {
			if (showNotification) {
				dispatch(
					notification(
						'Error',
						'There was an error updating your payment details.',
						true,
					),
				);
			}

			return {
				error: true,
				message:
					'There was an error updating your payment information. Please refresh the page and try again.',
			};
		}
	};

export const updateWebchatActive =
	({ isActive }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-webchat-status', {
				isActive,
			});

			const { title, message, error } = res.data;

			return dispatch(notification(title, message, error));
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your textchat status.',
					true,
				),
			);
		}
	};

export const updateWebchatGreeting =
	({
		webchatGreeting,
		frenchWebchatGreeting,
		spanishWebchatGreeting,
		displayGreetingMobile,
		displayGreetingDesktop,
		greetingColor,
	}) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-webchat-greeting', {
				webchatGreeting,
				frenchWebchatGreeting,
				spanishWebchatGreeting,
				displayGreetingMobile,
				displayGreetingDesktop,
				greetingColor,
			});

			const { title, message, error } = res.data;

			return dispatch(notification(title, message, error));
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your textchat greeting.',
					true,
				),
			);
		}
	};

export const updateWebchatWelcomeMessage =
	({
		webchatWelcomeMessage,
		frenchWebchatWelcomeMessage,
		spanishWebchatWelcomeMessage,
		welcomeMessageColor,
		showPoweredBy,
	}) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				'/api/update-webchat-welcome-message',
				{
					webchatWelcomeMessage,
					welcomeMessageColor,
					showPoweredBy,
					frenchWebchatWelcomeMessage,
					spanishWebchatWelcomeMessage,
				},
			);

			const { title, message, error } = res.data;

			return dispatch(notification(title, message, error));
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your textchat greeting.',
					true,
				),
			);
		}
	};

export const updateWebchatMultiLocationSettings =
	({ multiLocationHeading, multiLocationHeadingColor, locations }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				'/api/update-multi-location-webchat-settings',
				{
					multiLocationHeading,
					multiLocationHeadingColor,
					locations,
				},
			);

			const { title, message, error } = res.data;

			dispatch(notification(title, message, error));
			return { error: Boolean(error) };
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your multi-location Textchat.',
					true,
				),
			);
			return { error: true };
		}
	};

export const enableWebchatMultiLocation =
	({ locations }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/enable-multi-location-webchat', {
				locations,
			});

			const { title, message, error } = res.data;

			dispatch(notification(title, message, error));
			return { error: Boolean(error) };
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error enabling multi-location Textchat.',
					true,
				),
			);
			return { error: true };
		}
	};

export const updateWebchatBubble =
	({ webchatBubble }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-webchat-bubble', {
				webchatBubble,
			});

			const { title, message, error } = res.data;

			return dispatch(notification(title, message, error));
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your textchat bubble.',
					true,
				),
			);
		}
	};

export const updateWebchatPagePosition =
	({ webchatPagePosition }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-webchat-page-position', {
				webchatPagePosition,
			});

			const { title, message, error } = res.data;

			return dispatch(notification(title, message, error));
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your textchat page position.',
					true,
				),
			);
		}
	};

export const updateWebchatBubbleSize =
	({ bubbleSize }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-webchat-bubble-size', {
				bubbleSize,
			});

			const { title, message, error } = res.data;

			return dispatch(notification(title, message, error));
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your Textchat.',
					true,
				),
			);
		}
	};

export const updateReportEmails =
	({ reportEmails }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-report-emails', {
				reportEmails,
			});

			if (res.data && !res.data.error) {
				dispatch({ type: FETCH_USER, payload: res.data });
				const title = 'Success';
				const message = 'Updated report emails.';
				dispatch(notification(title, message, false));
			} else {
				dispatch(
					notification(
						'Error',
						'There was an error updating your report emails. Make sure your emails are formatted correctly.',
						true,
					),
				);
			}
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your report emails.',
					true,
				),
			);
		}
	};

export const displayNotification =
	({ title, message, error }) =>
	async (dispatch) => {
		dispatch(notification(title, message, error));
	};

export const cancelSubscription = (username, history) => async (dispatch) => {
	try {
		const res = await axios.post('/api/cancel-subscription', username);
		if (res.data === 'success') {
			history.push('/account-not-active');
		}
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true,
			),
		);
	}
};

export const removeFacebook = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/remove-facebook');
		if (res.data && res.data.error) {
			return dispatch(
				notification(
					'Error',
					'Sorry we could not remove your Facebook account.',
					true,
				),
			);
		}
		window.location.reload();
	} catch {
		dispatch(
			notification(
				'Error',
				'Sorry we could not remove your Facebook account.',
				true,
			),
		);
	}
};

export const removeCRM = (crm, displayName) => async (dispatch) => {
	const integrationName = displayName || crm;

	try {
		const res = await axios.post(`/api/remove-${crm.toLowerCase()}`);
		if (res.data && res.data.error) {
			return dispatch(
				notification(
					'Error',
					`Sorry we could not remove your ${integrationName} account.`,
					true,
				),
			);
		}
		window.location.reload();
	} catch {
		dispatch(
			notification(
				'Error',
				`Sorry we could not remove your ${integrationName} account.`,
				true,
			),
		);
	}
};

export const resubscribeToEmail = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/resubscribe-to-email');

		if (res.data && !res.data.error) {
			return dispatch(
				notification(
					'Success',
					'We have resubscribed you to receive emails.',
					false,
				),
			);
		}
		return dispatch(
			notification(
				'',
				'Unable resubscribed you to receive emails.',
				true,
			),
		);
	} catch {
		dispatch(
			notification(
				'Error',
				'Sorry we could not resubscribe you to receive emails.',
				true,
			),
		);
	}
};

export const removeGmb = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/remove-gmb');
		if (res.data && res.data.error) {
			return dispatch(
				notification(
					'Error',
					'Sorry we could not disconnect your Google My Business account.',
					true,
				),
			);
		}
		window.location.reload();
	} catch {
		dispatch(
			notification(
				'Error',
				'Sorry we could not disconnect your Google My Business account.',
				true,
			),
		);
	}
};

export const updateDefaultReviewRequestPhoto =
	({ defaultReviewRequestPhoto }) =>
	async (dispatch) => {
		try {
			const res = await axios.post(
				'/api/set-default-review-request-photo',
				{
					defaultReviewRequestPhoto,
				},
			);

			if (res.data && res.data.error) {
				return dispatch(
					notification(
						'Error',
						'There was an error updating your default photo.',
						true,
					),
				);
			}

			dispatch({ type: FETCH_USER, payload: res.data });

			const title = 'Success';
			const message = 'Updated default photo!';
			dispatch(notification(title, message, false));
		} catch {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true,
				),
			);
		}
	};
